import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getItemFromStorage } from '../../../helper/storage';
import { getEmployees } from '../../../redux/actions/employees';
import { getEngagement } from '../../../redux/actions/engagement';
import { listInvestments } from '../../../redux/actions/investments';
import { getCompanyStructure } from '../../../redux/actions/structure';
import { getTasks } from '../../../redux/actions/tasks';
import { getExpectedScores, getTopicFilteredEvolution } from '../../../redux/actions/topics';
import CompetencyChart from './CompetencyChart/CompetencyChart';
import './Dashboard.scss';
import DashboardFilters from './DashboardFilters/DashboardFilters';
import TeamMetrics from './TeamMetrics/TeamMetrics';

const Dashboard = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('Todos');
  const [competenciesData, setCompetenciesData] = useState([]);
  const [isLoadingCompetencies, setIsLoadingCompetencies] = useState(false);
  const employees = useSelector(state => state.employees);
  const investments = useSelector(state => state.investments);
  const structure = useSelector((state) => state?.structure?.data);
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    const companyId = getItemFromStorage("COMPANY_ID");
    if (!companyId) {
      history.push("/selecionar-empresa");
    }
  }, [history]);

  useEffect(() => {
    if (!Array.isArray(structure) || structure.length === 0) return;

    const unit = selectedDepartment || ((structure[0] && structure[0].name) || '');
    if (!unit) return;

    const filter = { unit };
    const isAllPosition = selectedPosition === 'Todos' || !selectedPosition;
    if (!isAllPosition) filter.role = selectedPosition;

    dispatch(listInvestments({ ...filter, pageSize: 100 }));
    dispatch(getEngagement());
    dispatch(getTasks(filter));
    dispatch(getEmployees(filter));
  }, [dispatch, structure, selectedPosition, selectedDepartment]);

  useEffect(() => {
    setIsLoadingCompetencies(true);
    const competenciesList = investments?.data?.items || [];
    if (competenciesList.length === 0) {
      setCompetenciesData([]);
      setIsLoadingCompetencies(false);
      return;
    }
    const results = competenciesList
      .filter(item => item?.id)
      .map(item => ({
        id: item.id,
        name: item.name,
        averageScore: item.tracking?.average || 0,
        trend: []
      }));
    setCompetenciesData(results);
    setIsLoadingCompetencies(false);
  }, [investments?.data?.items, selectedDepartment, selectedPosition]);

  useEffect(() => {
    dispatch(getCompanyStructure());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(structure) && structure.length > 0 && !selectedDepartment) {
      const first = structure[0];
      if (first && first.name) setSelectedDepartment(first.name);
    }
  }, [structure, selectedDepartment]);

  const filteredEmployees = useMemo(() => {
    if (!Array.isArray(employees?.data) || employees.isFetching) return [];
    return employees.data.filter(employee => {
      const isAllDepartment = !selectedDepartment || selectedDepartment === 'Todos';
      const isAllPosition = selectedPosition === 'Todos' || !selectedPosition;
      const departmentMatch = isAllDepartment || employee.department === selectedDepartment || employee.unit === selectedDepartment;
      const positionMatch = isAllPosition || employee.position === selectedPosition || employee.role === selectedPosition;
      return departmentMatch && positionMatch;
    });
  }, [selectedDepartment, selectedPosition, employees.data, employees.isFetching]);

  const filteredContentCount = useMemo(() => {
    return filteredEmployees.reduce((total, employee) => total + employee.contentCount, 0);
  }, [filteredEmployees]);

  const getFilteredCompetenciesData = competenciesData;

  const highestCompetency = getFilteredCompetenciesData.length > 0 ? getFilteredCompetenciesData.reduce((max, competency) => competency.averageScore > max.averageScore ? competency : max) : { id: '', name: '', averageScore: 0 };
  const lowestCompetency = getFilteredCompetenciesData.length > 0 ? getFilteredCompetenciesData.reduce((min, competency) => competency.averageScore < min.averageScore ? competency : min) : { id: '', name: '', averageScore: 0 };

  const [highestExpected, setHighestExpected] = useState('–');
  const [lowestExpected, setLowestExpected] = useState('–');
  const [loadingHighestExpected, setLoadingHighestExpected] = useState(false);
  const [loadingLowestExpected, setLoadingLowestExpected] = useState(false);

  useEffect(() => {
    if (!highestCompetency.id) {
      setHighestExpected('–');
      setLoadingHighestExpected(false);
      return;
    }
    setLoadingHighestExpected(true);
    const unit = selectedDepartment;
    const isAllPosition = selectedPosition === 'Todos' || !selectedPosition;
    if (!unit || isAllPosition) {
      setHighestExpected('–');
      setLoadingHighestExpected(false);
      return;
    }
    const payload = { unit };
    if (!isAllPosition) payload.role = selectedPosition;
    dispatch(getExpectedScores(highestCompetency.id, payload))
      .then(response => {
        const scoresArr = response?.expected_scores || [];
        const filtered = scoresArr.filter(s => {
          const depMatch = s.department === unit;
          const roleMatch = isAllPosition || s.role === selectedPosition;
          return depMatch && roleMatch;
        });
        setHighestExpected(filtered.length > 0 ? Number(filtered.reduce((sum, s) => sum + (s.expected_score || 0), 0) / filtered.length).toFixed(1) : '–');
      })
      .catch(() => setHighestExpected('–'))
      .finally(() => setLoadingHighestExpected(false));
  }, [dispatch, highestCompetency.id, selectedPosition, selectedDepartment]);

  useEffect(() => {
    if (!lowestCompetency.id) {
      setLowestExpected('–');
      setLoadingLowestExpected(false);
      return;
    }
    setLoadingLowestExpected(true);
    const unit = selectedDepartment;
    const isAllPosition = selectedPosition === 'Todos' || !selectedPosition;
    if (!unit || isAllPosition) {
      setLowestExpected('–');
      setLoadingLowestExpected(false);
      return;
    }
    const payload = { unit };
    if (!isAllPosition) payload.role = selectedPosition;
    dispatch(getExpectedScores(lowestCompetency.id, payload))
      .then(response => {
        const scoresArr = response?.expected_scores || [];
        const filtered = scoresArr.filter(s => {
          const depMatch = s.department === unit;
          const roleMatch = isAllPosition || s.role === selectedPosition;
          return depMatch && roleMatch;
        });
        setLowestExpected(filtered.length > 0 ? Number(filtered.reduce((sum, s) => sum + (s.expected_score || 0), 0) / filtered.length).toFixed(1) : '–');
      })
      .catch(() => setLowestExpected('–'))
      .finally(() => setLoadingLowestExpected(false));
  }, [dispatch, lowestCompetency.id, selectedPosition, selectedDepartment]);

  const teamMetrics = {
    highestCompetencyScore: {
      name: highestCompetency.name,
      score: Number(highestCompetency.averageScore?.toFixed(1) || 0),
      expected: highestExpected,
      loadingExpected: loadingHighestExpected
    },
    lowestCompetencyScore: {
      name: lowestCompetency.name,
      score: Number(lowestCompetency.averageScore?.toFixed(1) || 0),
      expected: lowestExpected,
      loadingExpected: loadingLowestExpected
    },
    contentItemsCount: filteredContentCount,
  };

  return (
    <div className="Dashboard">
      <h1 style={{ marginBottom: 40 }}>Dashboard</h1>
      <DashboardFilters
        selectedDepartment={selectedDepartment}
        selectedPosition={selectedPosition}
        onDepartmentChange={dep => {
          setSelectedDepartment(dep);
          setSelectedPosition('Todos');
        }}
        onPositionChange={setSelectedPosition}
        units={structure || []}
      />
      <TeamMetrics
        highestCompetencyScore={teamMetrics.highestCompetencyScore}
        lowestCompetencyScore={teamMetrics.lowestCompetencyScore}
        contentItemsCount={teamMetrics.contentItemsCount}
      />
      <CompetencyChart
        competencies={getFilteredCompetenciesData}
        selectedDepartmentFilter={selectedDepartment}
        selectedPositionFilter={selectedPosition}
        isLoading={investments?.isFetching || isLoadingCompetencies}
        onFetchEvolution={async (topicId) => {
          try {
            const isAllPosition = selectedPosition === 'Todos' || !selectedPosition;
            const teamFilters = { unit: selectedDepartment };
            if (!isAllPosition) teamFilters.role = selectedPosition;
            const [team, company] = await Promise.all([
              dispatch(getTopicFilteredEvolution(topicId, teamFilters)),
              dispatch(getTopicFilteredEvolution(topicId, {})),
            ]);
            return { team: team || [], company: company || [] };
          } catch {
            return { team: [], company: [] };
          }
        }}
        onFetchExpectedScore={async (topicId) => {
          try {
            const unit = selectedDepartment;
            const isAllPosition = selectedPosition === 'Todos' || !selectedPosition;
            if (!unit || isAllPosition) return null;
            const payload = { unit };
            if (!isAllPosition) payload.role = selectedPosition;
            const expectedResult = await dispatch(getExpectedScores(topicId, payload));
            const scoresArr = expectedResult?.expected_scores || [];
            const filtered = scoresArr.filter(s => s.department === unit && (isAllPosition || s.role === selectedPosition));
            return filtered.length > 0 ? Number(filtered.reduce((sum, s) => sum + (s.expected_score || 0), 0) / filtered.length).toFixed(1) : null;
          } catch {
            return null;
          }
        }}
      />
    </div>
  );
};

export default Dashboard;
